/**
 * Cliente da seção Transcrições.
 *
 * <p>Mesma divisão do acervo: abrir a página traz só metadado, e o arquivo desce quando
 * alguém abre aquela reunião.
 */
import { api, apiBlob } from '../../api/cliente';

const BASE = '/api/projetos/mistik-island/transcricoes';

/**
 * Os três formatos da fila. HTML entrou pelo diário de sessão — a página que o time
 * gera da reunião —, e é servido como texto plano: quem o renderiza é o
 * `QuadroHtmlIsolado`, num iframe de origem opaca.
 */
export type TipoTranscricao = 'TEXTO' | 'PDF' | 'HTML';

export interface Transcricao {
  id: number;
  tipo: TipoTranscricao;
  titulo: string;
  resumo?: string;
  nomeOriginal?: string;
  bytes: number;
  /** ISO (aaaa-mm-dd). É por ele que a fila é ordenada. */
  reuniaoEm: string;
  enviadoPor?: string;
  enviadoEm?: string;
}

export interface Fila {
  transcricoes: Transcricao[];
  total: number;
}

export const CHAVE_TRANSCRICOES = ['transcricoes'] as const;

export function listar(): Promise<Fila> {
  return api<Fila>(BASE);
}

export function conteudoBlob(id: number): Promise<Blob> {
  return apiBlob(`${BASE}/${id}/conteudo`);
}

export async function conteudoTexto(id: number): Promise<string> {
  return (await apiBlob(`${BASE}/${id}/conteudo`)).text();
}

export function enviar(dados: {
  arquivo: File;
  titulo?: string;
  resumo?: string;
  reuniaoEm?: string;
}): Promise<Transcricao> {
  const corpo = new FormData();
  corpo.append('arquivo', dados.arquivo);
  if (dados.titulo) corpo.append('titulo', dados.titulo);
  if (dados.resumo) corpo.append('resumo', dados.resumo);
  if (dados.reuniaoEm) corpo.append('reuniaoEm', dados.reuniaoEm);
  // Sem Content-Type à mão: o navegador precisa gerar o boundary do multipart.
  return api<Transcricao>(BASE, { method: 'POST', body: corpo });
}

export function salvar(id: number, mudancas: Record<string, unknown>): Promise<Transcricao> {
  return api<Transcricao>(`${BASE}/${id}`, { method: 'PATCH', body: JSON.stringify(mudancas) });
}

export function excluir(id: number): Promise<void> {
  return api<void>(`${BASE}/${id}`, { method: 'DELETE' });
}

/* Reexportado de `comum/`: a régua é uma só. Quem já importava daqui não muda. */
export { tamanhoLegivel } from '../../comum/tamanhoLegivel';

/** "12 de março de 2026" — a data por extenso, porque a lista é uma linha do tempo. */
export function dataPorExtenso(iso: string): string {
  // Sem `new Date(iso)` cru: um "2026-03-12" é lido como UTC e vira 11/03 em fuso
  // negativo. O Brasil inteiro está em fuso negativo.
  const [ano, mes, dia] = iso.split('-').map(Number);
  return new Date(ano, mes - 1, dia).toLocaleDateString('pt-BR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}
